// services/storeManagerService.ts
//
// Server-of-record for store-manager delegation: invite create /
// accept / revoke, permission updates, removal. Every mutation
// writes both the store-side and user-side rosters atomically via
// multi-path updates so a partial failure can't leave the two in
// disagreement.
//
// Read paths used elsewhere:
//   - subscribeStoreManagers(storeId)         — Store Managers page
//   - subscribeUserStoreManagerships(uid)     — useStorePermissions hook
//
// All writes here assume the *caller* has the right to mutate the
// store. The route gates + database rules are the authoritative
// permission checks; this service just rejects shape errors and
// the single-use guard on claim.

import {
  ref,
  get,
  set,
  update,
  remove,
  onValue,
  Unsubscribe,
} from 'firebase/database';
import { db } from '../config/firebase';
import {
  StoreManagerEntry,
  StoreManagership,
  StoreManagerInvite,
  StoreManagerPermissions,
  EMPTY_PERMISSIONS,
  INVITE_TTL_MS,
} from '../types/storeManager.types';

// ============================================================================
// INVITES
// ============================================================================

/**
 * Create a new single-use invite for the given store. Returns
 * the token (caller turns it into a link).
 *
 * The invite does NOT carry permissions — the store owner sets
 * those on the claimed manager AFTER the staff member accepts.
 * That's what the operator picked when we agreed on the model.
 */
export async function createStoreManagerInvite(
  storeId: string,
  storeName: string,
  storeOwnerUid: string,
  createdByUid: string,
): Promise<string> {
  // crypto.randomUUID is available everywhere we need (modern
  // browsers + Node 18+). 36 chars including dashes — fine as an
  // opaque path segment, no PII.
  const token = crypto.randomUUID();
  const now = Date.now();
  const invite: StoreManagerInvite = {
    token,
    storeId,
    storeName,
    storeOwnerUid,
    createdBy: createdByUid,
    createdAt: now,
    expiresAt: now + INVITE_TTL_MS,
    consumedAt: null,
    consumedByUid: null,
  };
  await set(ref(db, `storeManagerInvites/${token}`), invite);
  return token;
}

/** Look up an invite by token. Returns null when missing. */
export async function getStoreManagerInvite(
  token: string,
): Promise<StoreManagerInvite | null> {
  const snap = await get(ref(db, `storeManagerInvites/${token}`));
  if (!snap.exists()) return null;
  return snap.val() as StoreManagerInvite;
}

/**
 * Claim an invite as the currently-logged-in user.
 *
 * The fail conditions are checked in this exact order:
 *   1. Token doesn't exist                  → 'not found'
 *   2. Token expired                        → 'expired'
 *   3. Token already consumed               → 'already used'
 *   4. User already a manager for this store→ 'already a manager'
 *
 * On success, writes BOTH roster sides + marks the invite
 * consumed in a single multi-path update so a partial failure
 * can't drop the manager in just one place.
 *
 * Permissions start at EMPTY_PERMISSIONS — the store owner sets
 * the real ones from the manager list afterwards.
 */
export async function claimStoreManagerInvite(
  token: string,
  claimingUid: string,
  claimingUserName: string,
  claimingUserEmail: string | undefined,
): Promise<{ ok: true; storeId: string } | { ok: false; error: string }> {
  const invite = await getStoreManagerInvite(token);
  if (!invite) return { ok: false, error: 'Invite not found.' };
  if (Date.now() > invite.expiresAt) {
    return { ok: false, error: 'Invite has expired.' };
  }
  if (invite.consumedAt) {
    return { ok: false, error: 'Invite has already been used.' };
  }
  // Already-a-manager check is cheap — single get on the user-side
  // roster. Avoids re-adding someone who already claimed under a
  // different invite.
  const existingSnap = await get(
    ref(db, `users/${claimingUid}/storeManagerships/${invite.storeId}`),
  );
  if (existingSnap.exists()) {
    return { ok: false, error: 'You are already a manager for this store.' };
  }

  const now = Date.now();
  const managerEntry: StoreManagerEntry = {
    uid: claimingUid,
    name: claimingUserName,
    ...(claimingUserEmail ? { email: claimingUserEmail } : {}),
    permissions: { ...EMPTY_PERMISSIONS },
    assignedAt: now,
    assignedBy: invite.createdBy,
  };
  const managership: StoreManagership = {
    storeId: invite.storeId,
    storeName: invite.storeName,
    // Older invites (created before the storeOwnerUid field was
    // added) fall back to createdBy — for normal use those are
    // the same uid, so the fallback is harmless.
    storeOwnerUid: invite.storeOwnerUid || invite.createdBy,
    permissions: { ...EMPTY_PERMISSIONS },
    assignedAt: now,
  };

  await update(ref(db), {
    [`stores/${invite.storeId}/managers/${claimingUid}`]: managerEntry,
    [`users/${claimingUid}/storeManagerships/${invite.storeId}`]: managership,
    [`storeManagerInvites/${token}/consumedAt`]: now,
    [`storeManagerInvites/${token}/consumedByUid`]: claimingUid,
  });

  return { ok: true, storeId: invite.storeId };
}

/**
 * Revoke an unconsumed invite. No-op (returns silently) if the
 * invite was already consumed or doesn't exist — caller can blow
 * the dialog away either way.
 */
export async function revokeStoreManagerInvite(token: string): Promise<void> {
  const invite = await getStoreManagerInvite(token);
  if (!invite || invite.consumedAt) return;
  await remove(ref(db, `storeManagerInvites/${token}`));
}

/**
 * Subscribe to a store's outstanding (not-yet-consumed) invites.
 * Used by the Store Managers page to render the pending list.
 *
 * Firebase doesn't let us filter server-side on a missing key, so
 * we pull every invite for the store and filter in the callback.
 * Acceptable because outstanding invites per store will always be
 * a small handful.
 */
export function subscribeStoreManagerInvites(
  storeId: string,
  callback: (invites: StoreManagerInvite[]) => void,
): Unsubscribe {
  // Pulls all invites; client filters by storeId. Invites are an
  // admin-restricted path so this stays cheap.
  return onValue(ref(db, 'storeManagerInvites'), (snap) => {
    const all = (snap.val() as Record<string, StoreManagerInvite>) || {};
    const now = Date.now();
    const outstanding = Object.values(all).filter(
      (i) =>
        i.storeId === storeId &&
        !i.consumedAt &&
        i.expiresAt > now,
    );
    outstanding.sort((a, b) => b.createdAt - a.createdAt);
    callback(outstanding);
  });
}

// ============================================================================
// ROSTER
// ============================================================================

/** Subscribe to the active managers of a store. */
export function subscribeStoreManagers(
  storeId: string,
  callback: (managers: StoreManagerEntry[]) => void,
): Unsubscribe {
  return onValue(ref(db, `stores/${storeId}/managers`), (snap) => {
    const data = (snap.val() as Record<string, StoreManagerEntry>) || {};
    const list = Object.values(data).sort((a, b) => a.assignedAt - b.assignedAt);
    callback(list);
  });
}

/**
 * Subscribe to the stores a user is a manager for. Drives the
 * store picker on Tournament Setup / Series Create for proxies
 * who serve multiple stores.
 */
export function subscribeUserStoreManagerships(
  uid: string,
  callback: (memberships: StoreManagership[]) => void,
): Unsubscribe {
  return onValue(ref(db, `users/${uid}/storeManagerships`), (snap) => {
    const data = (snap.val() as Record<string, StoreManagership>) || {};
    const list = Object.values(data).sort((a, b) => a.assignedAt - b.assignedAt);
    callback(list);
  });
}

/**
 * Update the permissions for one manager. Writes both roster
 * sides atomically. Caller's authority is enforced by the DB
 * rule (store owner only).
 */
export async function setStoreManagerPermissions(
  storeId: string,
  uid: string,
  permissions: StoreManagerPermissions,
): Promise<void> {
  await update(ref(db), {
    [`stores/${storeId}/managers/${uid}/permissions`]: permissions,
    [`users/${uid}/storeManagerships/${storeId}/permissions`]: permissions,
  });
}

/**
 * Remove a manager from a store. Wipes both roster sides
 * atomically; their next attempt to act on behalf of this store
 * will fail the rule check.
 */
export async function removeStoreManager(
  storeId: string,
  uid: string,
): Promise<void> {
  await update(ref(db), {
    [`stores/${storeId}/managers/${uid}`]: null,
    [`users/${uid}/storeManagerships/${storeId}`]: null,
  });
}
